import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { CheckCircle, Clock, XCircle, Upload, FileText, Camera, Home, X, Loader2 } from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const Onboarding = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOnboardingStatus();
    fetchKYCStatus();
  }, []);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await api.get('/me/onboarding-status');
      setStatus(response.data);

      // Se já pode acessar, redireciona
      if (response.data.can_access_financial_features) {
        navigate('/app/dashboard');
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchKYCStatus = async () => {
    try {
      const response = await api.get('/me/kyc/status');
      setKycStatus(response.data);
    } catch (error) {
      console.error('Error fetching KYC:', error);
    }
  };

  const handleUploadDocument = async (docType, file) => {
    if (!file) return;
    
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/me/kyc/upload?doc_type=${docType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      toast({
        title: 'Documento enviado!',
        description: 'Seu documento foi enviado com sucesso.',
      });
      fetchKYCStatus();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        variant: 'destructive',
        title: 'Erro no upload',
        description: error.response?.data?.detail || 'Erro ao enviar documento. Tente novamente.',
      });
    } finally {
      setUploadingDoc(null);
    }
  };

  const openCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      setStream(mediaStream);
      setCameraOpen(true);
      
      // Wait for dialog to open and video element to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera error:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao acessar câmera',
        description: 'Não foi possível acessar sua câmera. Verifique as permissões ou envie uma foto.',
      });
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
        closeCamera();
        await handleUploadDocument('SELFIE', file);
      }
    }, 'image/jpeg', 0.8);
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
  };

  const handleSubmitKYC = async () => {
    try {
      await api.post('/me/kyc/submit');
      toast({
        title: 'KYC enviado para análise!',
        description: 'Aguarde a aprovação do banqueiro.',
      });
      fetchOnboardingStatus();
      fetchKYCStatus();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao enviar KYC',
      });
    }
  };

  const getStatusIcon = (statusValue) => {
    switch (statusValue) {
      case 'APPROVED':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'REJECTED':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'SUBMITTED':
      case 'UNDER_REVIEW':
        return <Clock className="h-5 w-5 text-warning" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (statusValue) => {
    const config = {
      APPROVED: { variant: 'default', label: 'Aprovado', className: 'bg-green-600' },
      REJECTED: { variant: 'destructive', label: 'Rejeitado' },
      SUBMITTED: { variant: 'secondary', label: 'Em Análise' },
      UNDER_REVIEW: { variant: 'secondary', label: 'Em Revisão' },
      NOT_STARTED: { variant: 'outline', label: 'Não Iniciado' },
      PENDING: { variant: 'outline', label: 'Pendente' },
      ACTIVE: { variant: 'default', label: 'Ativo', className: 'bg-green-600' },
      OPEN: { variant: 'default', label: 'Aberta', className: 'bg-green-600' },
      BLOCKED: { variant: 'secondary', label: 'Bloqueada' },
    };

    const { variant, label, className } = config[statusValue] || { variant: 'outline', label: statusValue || 'N/A' };
    return <Badge variant={variant} className={className}>{label}</Badge>;
  };

  const documentTypes = [
    { type: 'ID_FRONT', label: 'Documento (Frente)', description: 'RG, CNH ou outro documento com foto', icon: FileText, accept: 'image/*,.pdf' },
    { type: 'ID_BACK', label: 'Documento (Verso)', description: 'Verso do documento', icon: FileText, accept: 'image/*,.pdf' },
    { type: 'SELFIE', label: 'Selfie com Documento', description: 'Tire uma foto segurando seu documento', icon: Camera, isCamera: true },
    { type: 'PROOF_OF_ADDRESS', label: 'Comprovante de Endereço', description: 'Conta de luz, água ou extrato bancário', icon: Home, accept: 'image/*,.pdf' },
  ];

  const uploadedDocs = kycStatus?.documents || [];
  const progress = (uploadedDocs.length / documentTypes.length) * 100;
  const canSubmit = uploadedDocs.length >= 3 && kycStatus?.kyc_case?.status === 'NOT_STARTED';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 max-w-4xl" data-testid="onboarding-page">
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Processo de Abertura de Conta</h1>
          <p className="text-muted-foreground mt-2">Complete os passos abaixo para ativar sua conta</p>
        </div>

        {/* Status Overview */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {getStatusIcon(status?.kyc_status)}
              Status do Processo
            </CardTitle>
            <CardDescription>Acompanhe o progresso da sua solicitação</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status do Perfil:</span>
                  {getStatusBadge(status?.user_profile_status)}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status do Cliente:</span>
                  {getStatusBadge(status?.customer_status)}
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status do KYC:</span>
                  {getStatusBadge(status?.kyc_status)}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status da Conta:</span>
                  {getStatusBadge(status?.account_status)}
                </div>
              </div>
            </div>

            {status?.next_steps && status.next_steps.length > 0 && (
              <div className="rounded-lg bg-muted/50 p-4">
                <p className="text-sm font-medium mb-2">Próximos passos:</p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  {status.next_steps.map((step, idx) => (
                    <li key={idx}>{step}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* KYC Em Análise */}
        {status?.kyc_status === 'SUBMITTED' && (
          <Card className="border-yellow-500/50 bg-yellow-500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-600">
                <Clock className="h-5 w-5" />
                KYC em Análise
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Seus documentos foram enviados e estão sendo analisados pela nossa equipe.
                Você receberá uma notificação assim que o processo for concluído.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>Tempo estimado:</strong> até 24 horas úteis
              </p>
            </CardContent>
          </Card>
        )}

        {/* KYC Documents */}
        {status?.kyc_status !== 'APPROVED' && status?.kyc_status !== 'SUBMITTED' && (
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle>Documentação KYC</CardTitle>
              <CardDescription>Envie seus documentos para validação (JPG, PNG ou PDF)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Progresso:</span>
                  <span className="font-mono font-bold">{uploadedDocs.length}/{documentTypes.length}</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {documentTypes.map((doc) => {
                  const uploaded = uploadedDocs.find(d => d.doc_type === doc.type);
                  const isUploading = uploadingDoc === doc.type;
                  const Icon = doc.icon;

                  return (
                    <Card key={doc.type} className={`relative transition-all ${uploaded ? 'border-green-500/50 bg-green-500/5' : ''}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${
                            uploaded ? 'bg-green-500/20' : 'bg-muted'
                          }`}>
                            {isUploading ? (
                              <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            ) : (
                              <Icon className={`h-6 w-6 ${
                                uploaded ? 'text-green-600' : 'text-muted-foreground'
                              }`} strokeWidth={1.5} />
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium mb-1">{doc.label}</h4>
                            <p className="text-xs text-muted-foreground mb-2">{doc.description}</p>
                            
                            {uploaded ? (
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="text-xs text-green-600 font-medium">Enviado com sucesso</span>
                              </div>
                            ) : isUploading ? (
                              <span className="text-xs text-primary">Enviando...</span>
                            ) : doc.isCamera ? (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="h-8 rounded-full"
                                  onClick={openCamera}
                                  data-testid={`camera-${doc.type}`}
                                >
                                  <Camera className="h-3 w-3 mr-1" />
                                  Tirar Foto
                                </Button>
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    capture="user"
                                    onChange={(e) => handleUploadDocument(doc.type, e.target.files[0])}
                                  />
                                  <Button size="sm" variant="outline" className="h-8 rounded-full" asChild>
                                    <span>
                                      <Upload className="h-3 w-3 mr-1" />
                                      Enviar Arquivo
                                    </span>
                                  </Button>
                                </label>
                              </div>
                            ) : (
                              <label className="cursor-pointer inline-block">
                                <input
                                  type="file"
                                  className="hidden"
                                  accept={doc.accept}
                                  onChange={(e) => handleUploadDocument(doc.type, e.target.files[0])}
                                  data-testid={`upload-${doc.type}`}
                                />
                                <Button size="sm" variant="default" className="h-8 rounded-full" asChild>
                                  <span>
                                    <Upload className="h-3 w-3 mr-1" />
                                    Selecionar Arquivo
                                  </span>
                                </Button>
                              </label>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {canSubmit && (
                <Button
                  onClick={handleSubmitKYC}
                  className="w-full h-11 rounded-full"
                  data-testid="submit-kyc-button"
                >
                  Enviar para Análise
                </Button>
              )}

              {uploadedDocs.length > 0 && uploadedDocs.length < 3 && (
                <p className="text-sm text-muted-foreground text-center">
                  Envie pelo menos 3 documentos para submeter o KYC
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rejection Reason */}
        {status?.kyc_status === 'REJECTED' && kycStatus?.kyc_case?.rejection_reason && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardHeader>
              <CardTitle className="text-destructive">KYC Rejeitado</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                <strong>Motivo:</strong> {kycStatus.kyc_case.rejection_reason}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Por favor, corrija os documentos e envie novamente.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Camera Dialog */}
      <Dialog open={cameraOpen} onOpenChange={(open) => !open && closeCamera()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Tirar Selfie</DialogTitle>
            <DialogDescription>
              Posicione seu rosto no centro da câmera e segure seu documento próximo ao rosto
            </DialogDescription>
          </DialogHeader>
          
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            <canvas ref={canvasRef} className="hidden" />
            
            {/* Guide overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 border-2 border-white/50 rounded-full" />
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={closeCamera}>
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={capturePhoto}>
              <Camera className="h-4 w-4 mr-2" />
              Capturar Foto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Onboarding;
